import authService from './auth';
import api from './api';

/**
 * Servicio de dispositivos.
 *
 * Usa la instancia de axios configurada en `api.js` en lugar de `fetch` directo.
 * Antes cada método construía sus propias cabeceras y no pasaba por el
 * interceptor de respuesta, así que un 401 por token caducado terminaba en
 * error en vez de refrescar la sesión: la app fallaba o no según la ruta que
 * hubiera usado el componente. Ahora ambos caminos comparten autenticación,
 * refresco de token y manejo de errores.
 */
class DeviceService {
  /**
   * Obtener todos los dispositivos del usuario actual
   */
  async getUserDevices() {
    try {
      const userInfo = authService.getUserInfo();
      if (!userInfo) {
        throw new Error('Usuario no autenticado');
      }

      const { data } = await api.get(`users/${userInfo.sub}/devices`);
      return data.devices || [];
    } catch (error) {
      console.error('Error obteniendo dispositivos del usuario:', error);
      throw error;
    }
  }

  /**
   * Obtener información de un dispositivo específico
   */
  async getDeviceInfo(deviceId) {
    try {
      const { data } = await api.get(`devices/${deviceId}/config`);
      return data;
    } catch (error) {
      console.error('Error obteniendo información del dispositivo:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de un dispositivo
   */
  async getDeviceStatistics(deviceId, hours = 24) {
    try {
      const { data } = await api.get(`devices/${deviceId}/statistics`, {
        params: { hours },
      });
      return data;
    } catch (error) {
      console.error('Error obteniendo estadísticas del dispositivo:', error);
      throw error;
    }
  }

  /**
   * Obtener estado de la válvula
   */
  async getValveStatus(deviceId) {
    try {
      const { data } = await api.get(`devices/${deviceId}/valve/status`);
      return data;
    } catch (error) {
      console.error('Error obteniendo estado de la válvula:', error);
      throw error;
    }
  }

  /**
   * Controlar válvula mediante una acción explícita
   */
  async controlValve(deviceId, action) {
    try {
      if (!['open', 'close'].includes(action)) {
        throw new Error('Acción inválida. Use open o close');
      }

      const { data } = await api.post(`devices/${deviceId}/valve/control`, { action });
      return data;
    } catch (error) {
      console.error('Error controlando válvula:', error);
      throw error;
    }
  }

  /**
   * Actualizar configuración del dispositivo
   */
  async updateDeviceConfig(deviceId, config) {
    try {
      const { data } = await api.post(`devices/${deviceId}/config`, config);
      return data;
    } catch (error) {
      console.error('Error actualizando configuración:', error);
      throw error;
    }
  }

  /**
   * Obtener estado de salud del dispositivo
   */
  async getDeviceHealth(deviceId) {
    try {
      const { data } = await api.get(`devices/${deviceId}/health`);
      return data;
    } catch (error) {
      console.error('Error obteniendo salud del dispositivo:', error);
      throw error;
    }
  }

  /**
   * Obtener información del sistema del dispositivo
   */
  async getSystemInfo(deviceId) {
    try {
      const { data } = await api.get(`devices/${deviceId}/system/info`);
      return data;
    } catch (error) {
      console.error('Error obteniendo información del sistema:', error);
      throw error;
    }
  }

  /**
   * Reiniciar dispositivo
   */
  async restartDevice(deviceId) {
    try {
      const { data } = await api.post(`devices/${deviceId}/system/restart`);
      return data;
    } catch (error) {
      console.error('Error reiniciando dispositivo:', error);
      throw error;
    }
  }

  /**
   * Obtener perfil del usuario actual
   */
  async getUserProfile() {
    try {
      const { data } = await api.get('auth/profile');
      return data;
    } catch (error) {
      console.error('Error obteniendo perfil del usuario:', error);
      throw error;
    }
  }

  /**
   * Actualizar perfil del usuario
   */
  async updateUserProfile(profileData) {
    try {
      const { data } = await api.put('auth/profile', profileData);
      return data;
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      throw error;
    }
  }

  /**
   * Obtener todos los usuarios (solo admin)
   */
  async getAllUsers() {
    try {
      const { data } = await api.get('users');
      return data;
    } catch (error) {
      console.error('Error obteniendo usuarios:', error);
      throw error;
    }
  }

  /**
   * Agregar dispositivo a usuario (solo admin)
   */
  async addDeviceToUser(userId, deviceId) {
    try {
      const { data } = await api.post(`users/${userId}/devices`, { device_id: deviceId });
      return data;
    } catch (error) {
      console.error('Error agregando dispositivo a usuario:', error);
      throw error;
    }
  }

  /**
   * Remover dispositivo de usuario (solo admin)
   */
  async removeDeviceFromUser(userId, deviceId) {
    try {
      // NO DESPLEGADO: API Gateway sólo expone `/users/{user_id}/devices`, sin
      // el segmento del dispositivo. Esta llamada fallaría con un error de
      // CORS, no con un 404. Nadie la usa: la desvinculación va por
      // deviceRepository.unlink(), que falla de forma explícita.
      const { data } = await api.delete(`users/${userId}/devices/${deviceId}`);
      return data;
    } catch (error) {
      console.error('Error removiendo dispositivo de usuario:', error);
      throw error;
    }
  }

  /**
   * Verificar si el usuario tiene permisos de administrador
   */
  isAdmin() {
    const userInfo = authService.getUserInfo();
    if (!userInfo) return false;

    const groups = userInfo['cognito:groups'] || [];
    return groups.includes('Admins');
  }

  /**
   * Verificar si el usuario tiene permisos de operador
   */
  isOperator() {
    const userInfo = authService.getUserInfo();
    if (!userInfo) return false;

    const groups = userInfo['cognito:groups'] || [];
    return groups.includes('Operators') || groups.includes('Admins');
  }

  /**
   * Obtener rol del usuario
   */
  getUserRole() {
    const userInfo = authService.getUserInfo();
    if (!userInfo) return 'viewer';

    const groups = userInfo['cognito:groups'] || [];

    if (groups.includes('Admins')) return 'admin';
    if (groups.includes('Operators')) return 'operator';
    return 'viewer';
  }

  /**
   * Formatear datos de telemetría para gráficos
   */
  formatTelemetryForChart(telemetryData, field = 'flow_liters') {
    if (!telemetryData || !Array.isArray(telemetryData)) {
      return [];
    }

    return telemetryData.map(item => ({
      timestamp: new Date(item.timestamp * 1000),
      value: parseFloat(item[field] || 0),
      label: new Date(item.timestamp * 1000).toLocaleTimeString(),
    }));
  }

  /**
   * Calcular estadísticas resumidas
   */
  calculateSummaryStats(telemetryData) {
    if (!telemetryData || !Array.isArray(telemetryData) || telemetryData.length === 0) {
      return {
        totalFlow: 0,
        averageFlowRate: 0,
        maxFlowRate: 0,
        dataPoints: 0,
        timeRange: '0 horas',
      };
    }

    const flowValues = telemetryData.map(item => parseFloat(item.flow_liters || 0));
    const flowRates = telemetryData.map(item => parseFloat(item.flow_rate_lpm || 0));

    const totalFlow = Math.max(...flowValues);
    const averageFlowRate = flowRates.reduce((sum, rate) => sum + rate, 0) / flowRates.length;
    const maxFlowRate = Math.max(...flowRates);

    const oldestTimestamp = Math.min(...telemetryData.map(item => item.timestamp));
    const newestTimestamp = Math.max(...telemetryData.map(item => item.timestamp));
    const timeRangeHours = (newestTimestamp - oldestTimestamp) / 3600;

    return {
      totalFlow: totalFlow.toFixed(2),
      averageFlowRate: averageFlowRate.toFixed(2),
      maxFlowRate: maxFlowRate.toFixed(2),
      dataPoints: telemetryData.length,
      timeRange: `${timeRangeHours.toFixed(1)} horas`,
    };
  }
}

// Instancia única del servicio
const deviceService = new DeviceService();

export default deviceService;
